import { X } from "lucide-react";
import {
  Text,
  InputField,
  SocialLoginButton,
  IconButton,
} from "@components/atoms";

interface LoginStepProps {
  email: string;
  password: string;
  confirmPassword: string;
  step: "login" | "signup";
  onEmailChange: (email: string) => void;
  onPasswordChange: (password: string) => void;
  onConfirmPasswordChange: (password: string) => void;
  onEmailBlur: () => void;
  onPasswordBlur: () => void;
  onConfirmPasswordBlur: () => void;
  onToggleStep: () => void;
  onClose: () => void;
  isValid: boolean;
  onContinue: () => void;
  onGoogleLogin?: () => void;
    isGoogleLoading?: boolean;  // ← add this
  emailError?: string;
  passwordError?: string;
  confirmPasswordError?: string;
  authError?: string; // 👈 NUEVO
}

/**
 * Login/Signup step component for LoginModal
 */
export function LoginStep({
  email,
  password,
  confirmPassword,
  step,
  onEmailChange,
  onPasswordChange,
  onConfirmPasswordChange,
  onEmailBlur,
  onPasswordBlur,
  onConfirmPasswordBlur,
  onToggleStep,
  onClose,
  isValid,
  onContinue,
  onGoogleLogin,
  isGoogleLoading,
  emailError,
  passwordError,
  confirmPasswordError,
  authError,
}: LoginStepProps) {
  return (
    <>
      {/* Header */}
      <div className="flex items-center justify-between">
        <Text
          variant="h2"
          as="h2"
          className="font-semibold text-black"
          style={{ fontSize: 20 }}
        >
          {step === "login" ? "Iniciar sesión" : "Regístrate"}
        </Text>
        <IconButton size={24} onClick={onClose}>
          <X size={24} className="text-black" />
        </IconButton>
      </div>

      {/* Toggle link */}
      <div className="mt-2 flex items-center gap-1">
        <Text variant="body" as="span" className="text-neutral-40">
          {step === "login"
            ? "¿No tienes una cuenta?"
            : "¿Ya tienes una cuenta?"}
        </Text>
        <button
          className="cursor-pointer border-none bg-transparent text-body font-bold text-black underline"
          onClick={onToggleStep}
        >
          Ingresa aquí
        </button>
      </div>

      {/* Social login buttons */}
      <div className="mt-5 flex items-center justify-center">
        <SocialLoginButton
          provider="google"
          onClick={onGoogleLogin}
          disabled={!onGoogleLogin || isGoogleLoading}
        />
      </div>

      {/* Loading indicator shown while Google popup is open */}
      {isGoogleLoading && (
        <div className="mt-3 flex items-center justify-center gap-2 text-sm text-neutral-40">
          <svg
            className="animate-spin h-4 w-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12" cy="12" r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v8z"
            />
          </svg>
          Conectando con Google...
        </div>
      )}

      {/* Separator text for signup */}
      {step === "signup" && (
        <Text variant="body" as="p" className="mt-4 text-neutral-40">
          O regístrate con tu mail
        </Text>
      )}

      {/* Email */}
      <div className="mt-4">
        <InputField
          label="Correo electrónico"
          placeholder="ejemplo@email.com"
          type="email"
          value={email}
          onChange={(e) => onEmailChange(e.target.value)}
          onBlur={onEmailBlur}
          className="w-full"
        />
        {emailError && (
          <Text variant="body" as="p" className="mt-1 text-red-500 text-sm">
            {emailError}
          </Text>
        )}
      </div>

      {/* Password */}
      <div className="mt-3">
        <InputField
          label="Contraseña"
          placeholder={step === "signup" ? "Crear contraseña" : "Contraseña"}
          type="password"
          value={password}
          onChange={(e) => onPasswordChange(e.target.value)}
          onBlur={onPasswordBlur}
          className="w-full"
        />
        {passwordError && (
          <Text variant="body" as="p" className="mt-1 text-red-500 text-sm">
            {passwordError}
          </Text>
        )}
      </div>

      {/* Confirm password (signup only) */}
      {step === "signup" && (
        <div className="mt-3">
          <InputField
            label="Confirmar contraseña"
            placeholder="Confirmar contraseña"
            type="password"
            value={confirmPassword}
            onChange={(e) => onConfirmPasswordChange(e.target.value)}
            onBlur={onConfirmPasswordBlur}
            className="w-full"
          />
          {confirmPasswordError && (
            <Text variant="body" as="p" className="mt-1 text-red-500 text-sm">
              {confirmPasswordError}
            </Text>
          )}
        </div>
      )}

      {authError && (
        <Text
          variant="body"
          as="p"
          className="mt-3 text-red-500 text-sm text-center"
        >
          {authError}
        </Text>
      )}

      {/* Continue button */}
      <button
        className={`mt-5 flex w-full max-w-[335px] h-[50px] items-center justify-center rounded-[36px] text-h5 font-semibold transition-colors ${
          isValid && !isGoogleLoading
            ? "cursor-pointer bg-primary-110 text-white"
            : "cursor-not-allowed bg-neutral-20 text-neutral-70"
        }`}
        disabled={!isValid || isGoogleLoading}
        onClick={onContinue}
      >
        Continuar
      </button>
    </>
  );
}
